import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
    createSuccessStory,
    getAllSuccessStories,
    updateSuccessStory,
    deleteSuccessStory
} from '../Apis/SuccessApis';

const SuccessStories = () => {
    const [stories, setStories] = useState([]);
    const [open, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newStory, setNewStory] = useState({
        Thumbnail: null,
        YoutubeUrl: ''
    });
    const [editingId, setEditingId] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedStory, setSelectedStory] = useState(null);
    const [preview, setPreview] = useState(null);

    const fetchStories = async () => {
        setIsLoading(true);
        try {
            const response = await getAllSuccessStories();
            if (response.data && response.data.status) {
                setStories(response.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching success stories:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchStories();
    }, []);

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setNewStory({ Thumbnail: null, YoutubeUrl: '' });
        setEditingId(null);
        setPreview(null);
    };

    const handleMenuOpen = (event, story) => {
        setAnchorEl(event.currentTarget);
        setSelectedStory(story);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedStory(null);
    };

    const handleEdit = (story) => {
        setEditingId(story.id);
        setNewStory({
            Thumbnail: null,
            YoutubeUrl: story.YoutubeUrl || ''
        });
        setPreview(story.Thumbnail);
        handleMenuClose();
        setOpen(true);
    };

    const handleChange = (e) => {
        const { name, value, files, type } = e.target;
        if (type === 'file') {
            const file = files[0];
            setNewStory(prev => ({ ...prev, [name]: file }));
            if (file) {
                setPreview(URL.createObjectURL(file));
            }
        } else {
            setNewStory(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('YoutubeUrl', newStory.YoutubeUrl);
            if (newStory.Thumbnail) formData.append('Thumbnail', newStory.Thumbnail);

            if (editingId) {
                await updateSuccessStory(editingId, formData);
            } else {
                await createSuccessStory(formData);
            }

            handleClose();
            fetchStories();
        } catch (error) {
            console.error("Error saving success story:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (story) => {
        if (window.confirm('Are you sure you want to delete this success story?')) {
            try {
                await deleteSuccessStory(story.id);
                fetchStories();
            } catch (error) {
                console.error("Error deleting success story:", error);
            }
        }
        handleMenuClose();
    };

    return (
        <Container maxWidth="xl" >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" component="h1">
                    Success Stories
                </Typography>
                <Button variant="contained" color="primary" onClick={handleOpen}>
                    Add Success Story
                </Button>
            </Box>

            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Thumbnail</TableCell>
                                <TableCell>Youtube URL</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {stories.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">No stories found</TableCell>
                                </TableRow>
                            ) : (
                                stories.map((story, index) => (
                                    <TableRow key={story.id}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>
                                            <img src={story.Thumbnail} alt="Thumbnail" style={{ width: '80px', height: '45px', objectFit: 'cover', borderRadius: '4px' }} />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="primary" sx={{ textDecoration: 'none' }} component="a" href={story.YoutubeUrl} target="_blank">
                                                {story.YoutubeUrl}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <IconButton onClick={(e) => handleMenuOpen(e, story)}>
                                                <MoreVertIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={() => handleEdit(selectedStory)}>
                    <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Edit</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleDelete(selectedStory)}>
                    <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText>Delete</ListItemText>
                </MenuItem>
            </Menu>

            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{editingId ? 'Edit Success Story' : 'Add Success Story'}</DialogTitle>
                <DialogContent>
                    <TextField
                        margin="dense"
                        name="YoutubeUrl"
                        label="Youtube URL"
                        fullWidth
                        variant="outlined"
                        value={newStory.YoutubeUrl}
                        onChange={handleChange}
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        name="Thumbnail"
                        label="Thumbnail"
                        type="file"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                    />
                    {preview && (
                        <Box mb={2} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <img src={preview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }} />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={isSubmitting}>
                        {isSubmitting ? <CircularProgress size={24} /> : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default SuccessStories;
